import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { cn } from '@/lib/utils';
import { Spinner } from './Spinner';

/**
 * Botón del sistema de diseño.
 *
 * Estilo de la referencia: radios contenidos (10px), altura cómoda (40px el estándar),
 * sombra apenas perceptible y azul de marca sólido para la acción principal.
 */

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'subtle';
export type ButtonSize = 'sm' | 'md' | 'lg' | 'icon';

const variantStyles: Record<ButtonVariant, string> = {
  primary: 'bg-brand-600 text-white shadow-sm hover:bg-brand-700 active:bg-brand-800',
  secondary: 'border border-line-strong bg-surface text-ink-soft shadow-sm hover:bg-surface-muted',
  ghost: 'text-ink-muted hover:bg-surface-muted hover:text-ink-soft',
  danger: 'bg-danger text-white shadow-sm hover:bg-danger-strong',
  subtle: 'bg-brand-50 text-brand-700 hover:bg-brand-100',
};

const sizeStyles: Record<ButtonSize, string> = {
  sm: 'h-9 gap-1.5 px-3 text-caption',
  md: 'h-10 gap-2 px-4 text-body',
  lg: 'h-11 gap-2 px-5 text-body',
  icon: 'size-10 justify-center',
};

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** Muestra el spinner y bloquea el botón mientras hay una mutación en curso. */
  loading?: boolean;
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
}

export function Button({
  variant = 'primary',
  size = 'md',
  loading = false,
  leftIcon,
  rightIcon,
  className,
  children,
  disabled,
  type = 'button',
  ...props
}: ButtonProps) {
  const content =
    size === 'icon' ? (
      children
    ) : (
      <>
        {leftIcon}
        {children}
        {rightIcon}
      </>
    );

  return (
    <button
      type={type}
      disabled={disabled || loading}
      className={cn(
        'inline-flex shrink-0 items-center rounded-control font-medium transition-colors',
        'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-500',
        'disabled:cursor-not-allowed disabled:opacity-60',
        variantStyles[variant],
        sizeStyles[size],
        className,
      )}
      {...props}
    >
      {loading ? <Spinner className="size-4" /> : content}
    </button>
  );
}
